import {
  type IFilterOptions,
  type IOptionsProps,
  type IUseFilterProps,
  useFilters,
} from "@fluidattacks/design";
import _ from "lodash";
import { useCallback, useMemo } from "react";

import { useGetPackagesImages } from "./use-get-packages-images";

import type { IPackage } from "../../types";
import {
  type GetPackagesQueryVariables,
  ToePackagesStatusEnum,
} from "gql/graphql";
import { useFiltersStorage } from "storage/filters";
import {
  getCheckedFilter,
  getCheckedFilters,
  getNewFilter,
} from "utils/filters";
import { PACKAGE_MANAGER_OPTIONS } from "utils/package-managers";
import { translate } from "utils/translations/translate";

interface IPackageFilters {
  groupName: string;
  packages: IPackage[];
  loading: boolean;
  setBackFilters: React.Dispatch<
    React.SetStateAction<GetPackagesQueryVariables["filters"]>
  >;
}

const usePackagesFilters = ({
  groupName,
  packages,
  setBackFilters,
  loading,
}: IPackageFilters): IUseFilterProps<IPackage> => {
  const { images: imageOptions } = useGetPackagesImages(groupName);
  const { setValues, getValues } = useFiltersStorage();

  const options: IOptionsProps<IPackage>[] = useMemo(
    (): IOptionsProps<IPackage>[] => [
      {
        filterOptions: [
          {
            key: "platform",
            label: translate.t("group.tabs.toe.packages.allPackages.manager"),
            options: PACKAGE_MANAGER_OPTIONS,
            type: "checkboxes",
          },
        ],
        label: translate.t("group.tabs.toe.packages.allPackages.manager"),
      },
      {
        filterOptions: [
          {
            key: "vulnerable",
            label: translate.t(
              "group.tabs.toe.packages.allPackages.vulnerable",
            ),
            options: [
              { label: "Yes", value: "true" },
              { label: "No", value: "false" },
            ],
            type: "radioButton",
          },
        ],
        label: translate.t("group.tabs.toe.packages.allPackages.vulnerable"),
      },
      {
        filterOptions: [
          {
            key: "outdated",
            label: translate.t("group.tabs.toe.packages.allPackages.outdated"),
            options: [
              { label: "Yes", value: "true" },
              { label: "No", value: "false" },
            ],
            type: "radioButton",
          },
        ],
        label: translate.t("group.tabs.toe.packages.allPackages.outdated"),
      },
      {
        filterOptions: [
          {
            key: "rootNickname",
            label: translate.t(
              "group.tabs.toe.packages.allPackages.root.exact",
            ),
            type: "text",
          },
          {
            key: "partialNickname",
            label: translate.t(
              "group.tabs.toe.packages.allPackages.root.partial",
            ),
            type: "text",
          },
        ],
        label: translate.t("group.tabs.toe.packages.allPackages.root.label"),
      },
      {
        filterOptions: [
          {
            key: "images",
            label: "Images",
            options: imageOptions,
            type: "checkboxes",
          },
        ],
        label: "Images",
      },
    ],

    [imageOptions],
  );

  const handleGetValues = useCallback((): string | null => {
    return getValues("toe-packages", groupName) as string | null;
  }, [getValues, groupName]);

  const handleSetValues = useCallback(
    (value: string): void => {
      void setValues("toe-packages", value, groupName);
    },
    [setValues, groupName],
  );

  const handleFilterChange = useCallback(
    (newFilters: readonly IFilterOptions<IPackage>[]): void => {
      if (!loading) {
        const newFiltersArray = newFilters as IFilterOptions<IPackage>[];
        const platforms = getCheckedFilters(newFiltersArray, "platform");
        const vulnerable = getCheckedFilter(
          newFiltersArray,
          "vulnerable",
        )?.value;
        const outdated = getCheckedFilter(newFiltersArray, "outdated")?.value;
        const images = getCheckedFilters(newFiltersArray, "images");
        const rootNickname = getNewFilter(
          newFiltersArray,
          "rootNickname",
        )?.value;
        const partialNickname = getNewFilter(
          newFiltersArray,
          "partialNickname",
        )?.value;

        const newImages = [...(images ?? [])];

        const newRootNickname =
          rootNickname ??
          ((partialNickname ?? "") ? `*${partialNickname}*` : undefined);

        const newTags = [
          ...(vulnerable === "true" ? [ToePackagesStatusEnum.Vulnerable] : []),
          ...(outdated === "false" ? [ToePackagesStatusEnum.Updated] : []),
        ];

        const newTagsNot = [
          ...(vulnerable === "false" ? [ToePackagesStatusEnum.Vulnerable] : []),
          ...(outdated === "true" ? [ToePackagesStatusEnum.Updated] : []),
        ];

        const newBackFilters: Partial<GetPackagesQueryVariables["filters"]> = {
          images: newImages.length > 0 ? newImages : undefined,
          platforms:
            _.isEmpty(platforms) || _.isNil(platforms) ? undefined : platforms,
          rootNickname: newRootNickname,
          tags: newTags.length > 0 ? newTags : undefined,
          tagsNot: newTagsNot.length > 0 ? newTagsNot : undefined,
        };

        setBackFilters((prev): GetPackagesQueryVariables["filters"] => ({
          ...prev,
          ...newBackFilters,
        }));
      }
    },
    [loading, setBackFilters],
  );

  return useFilters({
    dataset: packages,
    getValues: handleGetValues,
    onFiltersChange: handleFilterChange,
    options,
    setValues: handleSetValues,
  });
};

export { usePackagesFilters };
